import { LoadingSpinner } from '@/shared/components/Loading';
import { useSystemStats, formatCents } from '@/services/referrals';

/**
 * System-wide referral analytics, per proposal §1.6 "Analytics" requirements:
 *   - system-wide referral counts
 *   - top referrers leaderboard (lives in its own tab)
 *   - conversion funnel (clicks → sign-ups → purchases → qualified)
 *   - revenue generated through referrals
 *   - tier distribution
 *
 * NB: clicks aren't tracked yet — the proposal mentions them but the data
 * model doesn't capture them. Funnel starts at sign-ups.
 */
const AnalyticsTab = () => {
    const { data, isLoading } = useSystemStats();
    if (isLoading) return <LoadingSpinner />;
    if (data?.status === 'error') return <div className="alert alert-danger">{data.error?.message || 'Failed to load analytics.'}</div>;
    if (!data?.data) return <div className="text-muted">No stats available.</div>;
    const stats = data.data;
    const totalRefs = stats.totalReferrals || 0;
    const totalTier = stats.tierDistribution.tier1 + stats.tierDistribution.tier2 + stats.tierDistribution.tier3;
    const tierPct = (n: number) => (totalTier > 0 ? Math.round((n / totalTier) * 100) : 0);

    return (
        <div>
            {/* Top metrics */}
            <div className="row g-3 mb-4">
                <KpiCard label="Total referrals" value={totalRefs.toLocaleString()} sub="All-time, all statuses" />
                <KpiCard label="Active referrers" value={stats.activeReferrers.toLocaleString()} sub="With ≥ 1 qualified sign-up" />
                <KpiCard
                    label="Lifetime commission"
                    value={formatCents(stats.commissionGenerated.lifetimeCents)}
                    sub="Net qualified earnings"
                    accent
                />
                <KpiCard label="Pending balance (system)" value={formatCents(stats.commissionGenerated.pendingCents)} sub="Within 30-day windows" />
            </div>

            {/* Funnel */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title">Conversion funnel</h5>
                    <p className="text-muted small mb-3">From sign-up to qualified commission. Clicks are not tracked at this time.</p>
                    <FunnelBar label="Signed up" value={stats.funnel.signedUp} max={stats.funnel.signedUp} color="#5EEAD4" />
                    <FunnelBar
                        label="Purchased"
                        value={stats.funnel.purchased}
                        max={stats.funnel.signedUp}
                        color="#2DD4BF"
                        rate={stats.funnel.signedUp > 0 ? (stats.funnel.purchased / stats.funnel.signedUp) * 100 : 0}
                    />
                    <FunnelBar
                        label="Qualified"
                        value={stats.funnel.qualified}
                        max={stats.funnel.signedUp}
                        color="#0D9488"
                        rate={stats.funnel.purchased > 0 ? (stats.funnel.qualified / stats.funnel.purchased) * 100 : 0}
                    />
                </div>
            </div>

            {/* Tier distribution + commission breakdown */}
            <div className="row g-3">
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h5 className="card-title">Tier distribution</h5>
                            <p className="text-muted small mb-3">{totalTier.toLocaleString()} referrers across the three tiers.</p>
                            <TierBar
                                label="Tier 1 · Practitioner"
                                value={stats.tierDistribution.tier1}
                                pct={tierPct(stats.tierDistribution.tier1)}
                                color="#94A3B8"
                            />
                            <TierBar
                                label="Tier 2 · Ambassador"
                                value={stats.tierDistribution.tier2}
                                pct={tierPct(stats.tierDistribution.tier2)}
                                color="#2DD4BF"
                            />
                            <TierBar
                                label="Tier 3 · Circle"
                                value={stats.tierDistribution.tier3}
                                pct={tierPct(stats.tierDistribution.tier3)}
                                color="#D4AF37"
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="card h-100">
                        <div className="card-body">
                            <h5 className="card-title">Referral status breakdown</h5>
                            <p className="text-muted small mb-3">Status of {totalRefs.toLocaleString()} attributed referrals.</p>
                            <StatusRow label="Qualified" value={stats.qualifiedReferrals} total={totalRefs} color="#10B981" />
                            <StatusRow label="Pending" value={stats.pendingReferrals} total={totalRefs} color="#F59E0B" />
                            <StatusRow label="Churned" value={stats.churnedReferrals} total={totalRefs} color="#EF4444" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const KpiCard = ({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: boolean }) => (
    <div className="col-md-3 col-sm-6">
        <div className="card h-100">
            <div className="card-body">
                <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.08em' }}>
                    {label}
                </div>
                <div className={`fs-4 fw-semibold mt-2 ${accent ? 'text-success' : ''}`}>{value}</div>
                {sub && <div className="text-muted small mt-1">{sub}</div>}
            </div>
        </div>
    </div>
);

const FunnelBar = ({ label, value, max, color, rate }: { label: string; value: number; max: number; color: string; rate?: number }) => {
    const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
    return (
        <div className="mb-2">
            <div className="d-flex justify-content-between small mb-1">
                <span>{label}</span>
                <span>
                    {value.toLocaleString()}
                    {rate !== undefined && rate > 0 ? ` (${rate.toFixed(1)}%)` : ''}
                </span>
            </div>
            <div style={{ background: 'rgba(255, 255, 255, 0.08)', borderRadius: 6, height: 22 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6 }} />
            </div>
        </div>
    );
};

const TierBar = ({ label, value, pct, color }: { label: string; value: number; pct: number; color: string }) => (
    <div className="mb-2">
        <div className="d-flex justify-content-between small mb-1">
            <span>{label}</span>
            <span>
                {value.toLocaleString()} · {pct}%
            </span>
        </div>
        <div style={{ background: 'rgba(255, 255, 255, 0.08)', borderRadius: 6, height: 14 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 6 }} />
        </div>
    </div>
);

const StatusRow = ({ label, value, total, color }: { label: string; value: number; total: number; color: string }) => {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    return (
        <div className="d-flex align-items-center gap-3 mb-2">
            <span className="d-inline-block" style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
            <span className="flex-grow-1">{label}</span>
            <span className="text-muted">
                {value.toLocaleString()} · {pct}%
            </span>
        </div>
    );
};

export default AnalyticsTab;
